<template>
  <div class="register-page">
    <!-- Toast 提示 -->
    <div v-if="showToast" class="toast" :class="toastType">
      <span>{{ toastMessage }}</span>
    </div>

    <div class="container">
      <div class="register-container">
        <div class="register-card">
          <h2 class="register-title text-center">注册闪讯达</h2>
          <p class="register-subtitle">创建您的账户，开始使用</p>

          <form @submit.prevent="handleRegister" class="register-form">
            <!-- 手机号 -->
            <div class="form-group">
              <label for="phone">手机号</label>
              <div class="input-with-icon">
                <input
                  type="tel"
                  id="phone"
                  v-model="form.phone"
                  placeholder="请输入手机号"
                  maxlength="11"
                  required
                  @blur="checkPhone"
                />
              </div>
              <div v-if="phoneError" class="error-message">{{ phoneError }}</div>
            </div>

            <!-- 短信验证码 -->
            <div class="form-group">
              <label for="smsCode">短信验证码</label>
              <div class="input-with-code">
                <input
                  type="text"
                  id="smsCode"
                  v-model="form.smsCode"
                  placeholder="请输入短信验证码"
                  maxlength="6"
                  required
                />
                <button
                  type="button"
                  class="code-btn"
                  :disabled="codeCooldown > 0"
                  @click="sendSMSCode"
                >
                  {{ codeBtnText }}
                </button>
              </div>
            </div>

            <!-- 密码 -->
            <div class="form-group">
              <label for="password">密码</label>
              <div class="input-with-icon">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  id="password"
                  v-model="form.password"
                  placeholder="请输入密码（6-20位字符）"
                  required
                  @input="checkPassword"
                />
                <button
                  type="button"
                  class="toggle-password"
                  @click="showPassword = !showPassword"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path v-if="showPassword" d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor"/>
                    <path v-else d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
              <div v-if="passwordError" class="error-message">{{ passwordError }}</div>
              <div v-if="passwordStrength" class="password-strength">
                <div class="strength-text">密码强度: {{ passwordStrength }}</div>
                <div class="strength-bar">
                  <div class="strength-fill" :class="passwordStrengthClass"></div>
                </div>
              </div>
            </div>

            <!-- 确认密码 -->
            <div class="form-group">
              <label for="confirmPassword">确认密码</label>
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                id="confirmPassword"
                v-model="form.confirmPassword"
                placeholder="请再次输入密码"
                required
                @input="checkConfirmPassword"
              />
              <div v-if="confirmPasswordError" class="error-message">{{ confirmPasswordError }}</div>
            </div>

            <!-- 协议 -->
            <div class="agreement">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.agreed" required />
                <span>我已阅读并同意</span>
              </label>
              <div class="agreement-links">
                <button type="button" @click="showAgreement">《用户协议》</button>
                和
                <button type="button" @click="showPrivacy">《隐私政策》</button>
              </div>
            </div>

            <!-- 提交按钮 -->
            <button type="submit" class="submit-btn" :disabled="loading">
              {{ loading ? '注册中...' : '立即注册' }}
            </button>

            <!-- 登录链接 -->
            <div class="login-link">
              已有账户？
              <button type="button" @click="goLogin">立即登录</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegisterPage',
  props: {
    logoUrl: {
      type: String,
      default: ''
    }
  },
  emits: ['go-back'], // 触发 go-back 事件跳转到登录页面
  data() {
    return {
      form: {
        phone: '',
        smsCode: '',
        password: '',
        confirmPassword: '',
        agreed: false
      },
      showPassword: false,
      showConfirmPassword: false,
      codeCooldown: 0,
      loading: false,
      phoneError: '',
      passwordError: '',
      confirmPasswordError: '',
      passwordStrength: '',
      passwordStrengthClass: '',
      codeTimer: null,
      // Toast 相关
      showToast: false,
      toastMessage: '',
      toastType: 'success', // success, error, info
      toastTimer: null
    }
  },
  computed: {
    codeBtnText() {
      if (this.codeCooldown > 0) {
        return `${this.codeCooldown}秒后重发`
      }
      return '获取验证码'
    }
  },
  mounted() {
    // 页面加载时检查
  },
  beforeUnmount() {
    if (this.codeTimer) {
      clearInterval(this.codeTimer)
    }
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
    }
  },
  methods: {
    goLogin() {
      this.$emit('go-back') // 触发 go-back 事件，父组件会处理跳转到登录页面
    },
    
    checkPhone() {
      const phone = this.form.phone.trim()
      if (!phone) {
        this.phoneError = '手机号不能为空'
        return false
      }
      
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        this.phoneError = '请输入正确的手机号'
        return false
      }
      
      this.phoneError = ''
      return true
    },
    
    checkPassword() {
      const password = this.form.password
      
      if (!password) {
        this.passwordError = '密码不能为空'
        this.passwordStrength = ''
        return false
      }
      
      if (password.length < 6 || password.length > 20) {
        this.passwordError = '密码长度需在6-20位之间'
        this.passwordStrength = ''
        return false
      }
      
      this.passwordError = ''
      
      // 检查密码强度
      let strength = 0
      if (/[a-z]/.test(password)) strength++ // 小写字母
      if (/[A-Z]/.test(password)) strength++ // 大写字母
      if (/[0-9]/.test(password)) strength++ // 数字
      if (/[^a-zA-Z0-9]/.test(password)) strength++ // 特殊字符
      
      if (password.length >= 12) strength++ // 长度奖励
      
      if (strength <= 2) {
        this.passwordStrength = '弱'
        this.passwordStrengthClass = 'weak'
      } else if (strength <= 4) {
        this.passwordStrength = '中'
        this.passwordStrengthClass = 'medium'
      } else {
        this.passwordStrength = '强'
        this.passwordStrengthClass = 'strong'
      }
      
      return true
    },
    
    checkConfirmPassword() {
      if (this.form.password !== this.form.confirmPassword) {
        this.confirmPasswordError = '两次输入的密码不一致'
        return false
      }
      
      this.confirmPasswordError = ''
      return true
    },
    
    // 显示 Toast 提示
    showToastMessage(message, type = 'info') {
      // 清除已有的定时器
      if (this.toastTimer) {
        clearTimeout(this.toastTimer)
      }
      
      this.toastMessage = message
      this.toastType = type
      this.showToast = true
      
      // 3秒后自动隐藏
      this.toastTimer = setTimeout(() => {
        this.showToast = false
      }, 3000)
    },
    
    // 使用 Bmob 发送短信验证码
    async sendSMSCode() {
      const phone = this.form.phone.trim()
      
      // 简单验证手机号格式
      if (!phone) {
        this.showToastMessage('请输入手机号', 'error')
        return
      }
      
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        this.showToastMessage('请输入正确的手机号', 'error')
        return
      }
      
      try {
        const Bmob = this.$bmob
        if (!Bmob) {
          this.showToastMessage('系统初始化异常，请刷新页面重试', 'error')
          return
        }
        
        // 使用 Bmob.requestSmsCode 发送验证码
        const params = {
          mobilePhoneNumber: phone
        }
        
        // 开始倒计时
        this.codeCooldown = 60
        this.codeTimer = setInterval(() => {
          if (this.codeCooldown > 0) {
            this.codeCooldown--
          } else {
            clearInterval(this.codeTimer)
            this.codeTimer = null
          }
        }, 1000)
        
        // 发送验证码
        const response = await Bmob.requestSmsCode(params)
        console.log('短信验证码发送成功:', response)
        
        this.showToastMessage('验证码已发送，请注意查收短信', 'success')
      } catch (error) {
        console.error('发送短信验证码失败:', error)
        let errorMsg = '发送验证码失败'
        
        // 清除倒计时
        clearInterval(this.codeTimer)
        this.codeTimer = null
        this.codeCooldown = 0
        
        if (error && error.error) {
          // Bmob 错误信息处理
          switch(error.error) {
            case '手机号码格式不正确':
              errorMsg = '手机号码格式不正确'
              break
            case '每个手机号每天最多发送10条短信':
              errorMsg = '该手机号今天发送次数已达上限'
              break
            case '一分钟内不能重复发送':
              errorMsg = '发送太频繁，请稍后再试'
              break
            default:
              errorMsg = error.error
          }
        }
        
        this.showToastMessage(errorMsg, 'error')
      }
    },
    
    showAgreement() {
      this.showToastMessage('用户协议弹窗', 'info')
    },
    
    showPrivacy() {
      this.showToastMessage('隐私政策弹窗', 'info')
    },
    
    // 使用 Bmob 验证短信验证码并注册
    async handleRegister() {
      // 验证所有字段
      if (!this.checkPhone() || !this.checkPassword() || !this.checkConfirmPassword()) {
        this.showToastMessage('请修正表单中的错误', 'error')
        return
      }
      
      if (!this.form.smsCode || this.form.smsCode.length !== 6) {
        this.showToastMessage('请输入6位短信验证码', 'error')
        return
      }
      
      if (!this.form.agreed) {
        this.showToastMessage('请阅读并同意用户协议和隐私政策', 'error')
        return
      }
      
      this.loading = true
      
      try {
        const Bmob = this.$bmob
        if (!Bmob) {
          throw new Error('系统初始化异常')
        }
        
        // 1. 验证短信验证码
        const verifyData = {
          mobilePhoneNumber: this.form.phone
        }
        
        console.log('开始验证短信验证码...')
        await Bmob.verifySmsCode(this.form.smsCode, verifyData)
        console.log('短信验证码验证成功')
        
        // 2. 使用 Bmob.User.register 注册用户
        // 根据 Bmob 文档，注册需要邮箱，我们将使用手机号生成虚拟邮箱
        const params = {
          username: this.form.phone, // 用户名（使用手机号）
          password: this.form.password,
          email: `${this.form.phone}@tradesoeasy.com`, // 必填，使用虚拟邮箱
          phone: this.form.phone // 可选，手机号
        }
        
        console.log('开始注册用户，参数:', params)
        const user = await Bmob.User.register(params)
        console.log('用户注册成功:', user)
        
        // 注册成功后显示提示信息
        this.showToastMessage('注册成功！请登录', 'success')
        
        // 延迟一下，让用户看到成功提示
        setTimeout(() => {
          // 注册成功后跳转到登录页面
          this.goLogin() // 这会触发 go-back 事件，父组件会跳转到登录页面
        }, 1500)
        
      } catch (error) {
        console.error('注册失败:', error)
        let errorMsg = '注册失败，请重试'
        
        if (error && error.error) {
          switch(error.error) {
            case '验证码错误':
              errorMsg = '短信验证码错误'
              break
            case '手机号码格式不正确':
              errorMsg = '手机号码格式不正确'
              break
            case '手机号码已经被注册':
              errorMsg = '该手机号已被注册'
              break
            case '用户名已经存在':
              errorMsg = '该手机号已被注册'
              break
            case '邮箱已经存在':
              errorMsg = '该手机号已被注册'
              break
            case 'content is empty.':
              errorMsg = '注册信息不完整'
              break
            default:
              errorMsg = error.error || '注册失败，请重试'
          }
        } else if (error && error.message) {
          errorMsg = error.message
        }
        
        this.showToastMessage(errorMsg, 'error')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
<style scoped>
.register-page {
  min-height: calc(100vh - 180px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  position: relative;
}

/* Toast 样式 */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s ease, fadeOut 0.3s ease 2.7s;
  animation-fill-mode: forwards;
  min-width: 200px;
  text-align: center;
}

.toast.success {
  background-color: #52c41a;
}

.toast.error {
  background-color: #ff4d4f;
}

.toast.info {
  background-color: #1890ff;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translate(-50%, -20px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    transform: translate(-50%, 0);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -20px);
  }
}

.register-container {
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.register-card {
  width: 100%;
  max-width: 450px;
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.register-title {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  text-align: center;
}

.register-subtitle {
  color: #666;
  text-align: center;
  margin-bottom: 32px;
}

.register-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.input-with-icon,
.input-with-code {
  position: relative;
}

.input-with-icon .input-prefix {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 14px;
}

.input-with-icon input {
  padding-left: 60px;
}

/* 使用更具体的选择器避免全局样式影响 */
.register-page .register-form .input-with-code {
  display: flex !important;
  flex-direction: row !important;
  gap: 12px;
}

.register-page .register-form .input-with-code input {
  flex: 1 !important;
  min-width: 150px !important;
}

.register-page .register-form input[type="tel"],
.register-page .register-form input[type="text"],
.register-page .register-form input[type="password"] {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
  box-sizing: border-box !important;
}

.register-page .register-form input:focus {
  outline: none !important;
  border-color: #540cfa !important;
  box-shadow: 0 0 0 2px rgba(84, 12, 250, 0.1) !important;
}

.toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
}

.code-btn {
  padding: 12px 20px;
  border: 1px solid #540cfa;
  border-radius: 8px;
  background: white;
  color: #540cfa;
  font-size: 14px;
  cursor: pointer;
  min-width: 100px;
  transition: all 0.3s;
  white-space: nowrap;
  flex-shrink: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.code-btn:hover:not(:disabled) {
  background: #540cfa;
  color: white;
}

.code-btn:disabled {
  border-color: #dcdfe6;
  color: #999;
  cursor: not-allowed;
}

.error-message {
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
}

.password-strength {
  margin-top: 8px;
}

.strength-text {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.strength-bar {
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s, background-color 0.3s;
}

.strength-fill.weak {
  width: 33%;
  background: #ff4d4f;
}

.strength-fill.medium {
  width: 66%;
  background: #faad14;
}

.strength-fill.strong {
  width: 100%;
  background: #52c41a;
}

/* 协议部分 - 修改为浮动布局 */
.register-page .register-form .agreement {
  /* display: flex !important; 注释掉这行 */
  font-size: 14px;
  color: #666;
  overflow: hidden; /* 清除浮动 */
}

.register-page .register-form .agreement .checkbox-label {
  float: left !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px;
  cursor: pointer;
  white-space: nowrap !important;
  margin-right: 4px !important;
}

.register-page .register-form .checkbox-label input[type="checkbox"] {
  width: auto !important;
  margin: 0 !important;
  flex-shrink: 0 !important;
}

.register-page .register-form .agreement .agreement-links {
  float: left !important;
  display: flex !important;
  align-items: center !important;
  gap: 4px !important;
  white-space: nowrap !important;
}

.register-page .register-form .agreement-links button {
  background: none !important;
  border: none !important;
  color: #540cfa !important;
  cursor: pointer !important;
  padding: 0 !important;
  font-size: 14px !important;
  white-space: nowrap !important;
}

.submit-btn {
  padding: 14px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
  clear: both; /* 清除浮动 */
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-link {
  text-align: center;
  color: #666;
  font-size: 14px;
}

.login-link button {
  background: none;
  border: none;
  color: #540cfa;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
}

/* 响应式设计 - 关键修改部分 */
@media (max-width: 768px) {
  .register-page {
    min-height: calc(100vh - 140px);
    padding: 20px 15px !important;
  }
  
  .register-container {
    align-items: flex-start !important;
  }
  
  .register-card {
    padding: 30px 20px !important;
    margin: 0 !important;
    max-width: 100% !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
  }
  
  .register-title {
    font-size: 24px !important;
    margin-bottom: 6px !important;
  }
  
  .register-subtitle {
    margin-bottom: 25px !important;
  }
  
  .register-form {
    gap: 16px !important;
  }
  
  /* 关键：确保验证码输入框和按钮在同一行且比例合适 */
  .register-page .register-form .input-with-code {
    flex-direction: row !important;
    gap: 8px !important;
    width: 100% !important;
  }
  
  .register-page .register-form .input-with-code input {
    flex: 0 0 65% !important; /* 输入框占据65%宽度 */
    min-width: 0 !important;
    width: 65% !important;
  }
  
  .code-btn {
    flex: 0 0 30% !important; /* 按钮占据30%宽度 */
    min-width: 0 !important;
    width: 30% !important;
    padding: 12px 6px !important;
    font-size: 13px !important;
    flex-shrink: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    line-height: 1.2;
  }
  
  /* 关键：确保协议在一行显示 */
  .register-page .register-form .agreement {
    font-size: 13px !important;
    overflow: hidden;
  }
  
  .register-page .register-form .agreement .checkbox-label {
    font-size: 13px !important;
    float: left !important;
    margin-right: 2px !important;
  }
  
  .register-page .register-form .agreement-links {
    gap: 2px !important;
    float: left !important;
  }
  
  .register-page .register-form .agreement-links button {
    font-size: 13px !important;
  }
  
  /* 调整移动端输入框高度 */
  .register-page .register-form input[type="tel"],
  .register-page .register-form input[type="text"],
  .register-page .register-form input[type="password"] {
    height: 44px !important;
    font-size: 16px !important;
  }
  
  /* Toast 调整 */
  .toast {
    top: 70px !important;
    left: 15px !important;
    right: 15px !important;
    transform: none !important;
    min-width: auto !important;
    font-size: 13px !important;
    padding: 10px 16px !important;
  }
}

@media (max-width: 576px) {
  .register-card {
    padding: 25px 18px !important;
  }
  
  /* 关键：在小屏幕上确保验证码部分合适 */
  .register-page .register-form .input-with-code {
    flex-direction: row !important;
    gap: 6px !important;
    width: 100% !important;
  }
  
  .register-page .register-form .input-with-code input {
    flex: 0 0 70% !important; /* 输入框占据70%宽度 */
    width: 70% !important;
  }
  
  .code-btn {
    flex: 0 0 25% !important; /* 按钮占据25%宽度 */
    width: 25% !important;
    padding: 12px 4px !important;
    font-size: 13px !important;
    min-width: 0 !important;
  }
  
  /* 关键：在小屏幕上确保协议在一行 */
  .register-page .register-form .agreement {
    font-size: 13px !important;
    overflow: hidden;
  }
  
  .register-page .register-form .agreement .checkbox-label {
    gap: 4px !important;
    font-size: 13px !important;
    float: left !important;
  }
  
  .register-page .register-form .agreement-links button {
    font-size: 13px !important;
  }
  
  /* 调整密码可见性按钮位置 */
  .toggle-password {
    right: 8px !important;
  }
  
  /* 调整前缀样式 */
  .input-with-icon .input-prefix {
    left: 10px !important;
    font-size: 13px !important;
  }
  
  .input-with-icon input {
    padding-left: 50px !important;
  }
  
  /* 极端小屏幕下的调整 */
  @media (max-width: 360px) {
    .register-page .register-form .agreement {
      font-size: 10px !important;
    }
    
    .register-page .register-form .input-with-code input {
      flex: 0 0 68% !important;
      width: 68% !important;
    }
    
    .code-btn {
      flex: 0 0 27% !important;
      width: 27% !important;
      font-size: 10px !important;
      padding: 12px 2px !important;
    }
    
    .register-page .register-form .agreement-links button {
      font-size: 10px !important;
    }
  }
}
</style>